import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/ui/loading-state";

export const RevenueChart = () => {
  const { data: revenueData, isLoading, error } = useQuery({
    queryKey: ['revenue-data'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('revenue_data')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) return <LoadingState />;
  if (error) return <div>Error loading revenue data</div>;
  if (!revenueData?.length) return <div>No revenue data available</div>;

  // Calculate min and max values for better Y axis scaling
  const maxValue = Math.max(
    ...revenueData.map(item => Math.max(item.revenue, item.expenses))
  );
  const minValue = Math.min(
    ...revenueData.map(item => Math.min(item.revenue, item.expenses))
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl text-primary">Revenue & Expenses Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={revenueData} 
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00BFA6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#00BFA6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tick={{ fill: '#666' }}
                tickLine={{ stroke: '#666' }}
              />
              <YAxis 
                domain={[0, 10000000]}
                ticks={[0, 1000000, 5000000, 10000000]}
                tickFormatter={(value) => value === 0 ? '0' : `${value / 1000000}M`}
                tick={{ fill: '#666' }}
                tickLine={{ stroke: '#666' }}
                width={50}
              />
              <Tooltip 
                formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}
                labelStyle={{ color: '#334155' }}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#00BFA6" 
                fillOpacity={1} 
                fill="url(#colorRevenue)" 
                name="Revenue"
                strokeWidth={2}
              />
              <Area 
                type="monotone" 
                dataKey="expenses" 
                stroke="#0EA5E9" 
                fillOpacity={1} 
                fill="url(#colorExpenses)" 
                name="Expenses"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};